import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/siteData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);
  const activeArticles = articles.filter((a) => a.category === activeSlug);

  return (
    <div className="pt-16">
      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: activeCategory?.name || '分类' },
        ]}
      />

      {/* Category Header */}
      <section className="w-full py-10 md:py-14 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
            {activeCategory?.name || '内容分类'}
          </h1>
          <p className="text-sm md:text-base text-foreground-500 max-w-2xl">
            {activeCategory?.description}
          </p>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="w-full py-6 bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="flex items-center gap-2 overflow-x-auto flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveSlug(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-foreground-900 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                }`}
              >
                {cat.name}
                <span className="ml-1.5 text-xs opacity-70">({cat.count})</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="w-full py-10 md:py-14 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          {activeArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
              {activeArticles.map((article) => (
                <article
                  key={article.slug}
                  className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/70 transition-colors hover:border-background-300/70"
                >
                  <Link to={`/article/${article.slug}`} className="block">
                    <div className="w-full aspect-[16/10] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  </Link>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-foreground-400 mb-3">
                      <span>{article.categoryName}</span>
                      <span>·</span>
                      <span>{article.date}</span>
                    </div>
                    <h2 className="text-base font-bold font-heading text-foreground-900 mb-2 leading-snug group-hover:text-primary-600 transition-colors">
                      <Link to={`/article/${article.slug}`}>
                        {article.title}
                      </Link>
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-foreground-400 flex items-center gap-1">
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-time-line text-xs"></i>
                        </span>
                        {article.readTime}
                      </span>
                      <Link
                        to={`/article/${article.slug}`}
                        className="text-xs text-primary-600 font-medium hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
                      >
                        阅读
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-arrow-right-s-line"></i>
                        </span>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center text-foreground-300">
                <i className="ri-inbox-line text-4xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}